import {
    useLayoutEffect,
    useCallback,
    useContext,
    useEffect,
    useState,
    useMemo,
    useRef,
    type FC,
    useId
} from "react";
import {
    type PanResponderGestureState,
    type GestureResponderEvent,
    type LayoutChangeEvent,
    PanResponder,
    Animated,
    Platform,
    View
} from "react-native";
import type IChartPlotProps from "./type";
import type {
    ChartPlotResizeObserverConstructor,
    ChartPlotActiveChangeConstantType,
    ChartPlotKeyboardEventType,
    ChartPlotPointerEventType,
    ChartPlotNativePropsType,
    ChartPlotTooltipSnapshot,
    ChartPlotMeasurableNode,
    ChartPlotWebPropsType,
    ChartPlotSizeState,
    ChartPlotPoint
} from "./type";
import stylesheet, {
    getChartPlotTooltipMotionStyle,
    CHART_REFETCH_OPACITY,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import {
    useInteractionState
} from "../../../../helpers/interaction";
import {
    usePresence
} from "../../../../helpers/motion";
import type {
    ChartInputSource,
    ChartPlotLayout
} from "../../type";
import Svg from "react-native-svg";
import {
    getChartTooltipPosition,
    getChartKeyboardIndex,
    CHART_SKELETON_KINDS
} from "../../util";
import {
    ChartContext
} from "../../context";
import useChartEntrance from "../../useChartEntrance";
import ChartSkeleton from "../skeleton";
import ChartTooltip from "../tooltip";
import Text from "../../../text";

const CHART_PLOT_TOUCH_POINTER = "touch";

const CHART_PLOT_SVG_PROPS: Record<string, string> = Platform.OS === "web" ? {
    direction: "ltr"
} : {};

const CHART_PLOT_ACCESSIBILITY_ACTIONS = [
    {
        name: "increment"
    },
    {
        name: "decrement"
    }
];

const isMeasurableNode = (node: unknown): node is ChartPlotMeasurableNode => {
    return typeof node === "object" && node !== null && "getBoundingClientRect" in node && typeof node.getBoundingClientRect === "function";
};

const getResizeObserver = (): ChartPlotResizeObserverConstructor | undefined => {
    const observer: unknown = Reflect.get(globalThis, "ResizeObserver");

    return typeof observer === "function" ? observer as ChartPlotResizeObserverConstructor : undefined;
};

const ChartPlot: FC<IChartPlotProps> = ({
    isAnimatedOnMount: isAnimatedOnMountProp,
    tooltipBehaviour: tooltipBehaviourProp,
    customLocalize: customLocalizeProp,
    renderTooltip: renderTooltipProp,
    activeIndex: activeIndexProp,
    customTheme: customThemeProp,
    plotSize = "medium",
    seriesCount = 1,
    onActiveChange,
    skeletonKind,
    getReading,
    getTooltip,
    pointCount,
    getAnchor,
    children,
    getIndex,
    summary,
    style,
    ...props
}) => {
    const context = useContext(ChartContext);

    const customTheme = customThemeProp ?? context?.customTheme;

    const {
        surfaceColor: surfaceColorKey,
        shapeConfigs,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        direction,
        localize
    } = NCoreUIKitLocalize.useContext(customLocalizeProp ?? context?.customLocalize);

    const isRTL = direction === "rtl";

    const isWeb = Platform.OS === "web";

    const baseID = useId().replace(/[^a-zA-Z0-9]/g, "");

    const liveID = `${baseID}-live`;

    const hintID = `${baseID}-hint`;

    const containerRef = useRef<View>(null);

    const originRef = useRef<ChartPlotPoint>({
        x: 0,
        y: 0
    });

    const tooltipSnapshotRef = useRef<ChartPlotTooltipSnapshot | undefined>(undefined);

    const [
        size,
        setSize
    ] = useState<ChartPlotSizeState>({
        height: 0,
        width: 0
    });

    const [
        tooltipSize,
        setTooltipSize
    ] = useState<ChartPlotSizeState | undefined>(undefined);

    const [
        activeIndexState,
        setActiveIndexState
    ] = useState<number | undefined>(undefined);

    const [
        activeSeriesIndex,
        setActiveSeriesIndex
    ] = useState<number>(0);

    const [
        inputSource,
        setInputSource
    ] = useState<ChartInputSource | undefined>(undefined);

    const [
        pointerY,
        setPointerY
    ] = useState<number | undefined>(undefined);

    const {
        interactionHandlers,
        isFocusVisible
    } = useInteractionState();

    const plotHeight = shapeConfigs.chartHeight[plotSize];

    const setPlotHeight = context?.setPlotHeight;

    const isControlled = activeIndexProp !== undefined;

    const activeIndex = isControlled ? activeIndexProp ?? undefined : activeIndexState;

    const activeIndexRef = useRef<number | undefined>(activeIndex);

    activeIndexRef.current = activeIndex;

    const dataState = context?.dataState ?? "ready";

    const isLoading = context?.isLoading ?? false;

    const isFirstLoad = isLoading && pointCount === 0 && dataState === "ready";

    const isRefetching = isLoading && pointCount > 0;

    const isCovered = !!context && (context.isTableVisible || dataState !== "ready");

    const isInteractive = !isFirstLoad && !isCovered && pointCount > 0;

    const entrance = useChartEntrance({
        isReady: dataState === "ready" && !isLoading && pointCount > 0 && size.width > 0,
        isEnabled: isAnimatedOnMountProp ?? context?.isAnimatedOnMount ?? false,
        targetRef: containerRef,
        customTheme
    });

    const tooltipBehaviour = tooltipBehaviourProp ?? context?.tooltipBehaviour ?? "auto";

    const tooltipMode = tooltipBehaviour === "auto" ? inputSource === "touch" ? "pinned" : "follow" : tooltipBehaviour;

    const {
        container: containerDynamicStyle,
        live: liveDynamicStyle,
        svg: svgDynamicStyle
    } = useStyles({
        svgOpacity: entrance.opacity * (isRefetching ? CHART_REFETCH_OPACITY : 1),
        isFocusVisible,
        shapeConfigs,
        plotSize,
        radiuses,
        borders,
        colors
    });

    const layout: ChartPlotLayout = {
        surfaceColor: colors.content.container[surfaceColorKey],
        height: size.height,
        width: size.width,
        activeSeriesIndex,
        inputSource,
        activeIndex,
        entrance,
        isRTL
    };

    const handleSize = useCallback((next: ChartPlotSizeState) => {
        setSize((current) => current.height === next.height && current.width === next.width ? current : next);
    }, []);

    useLayoutEffect(() => {
        if(!isWeb) {
            return;
        }

        const node: unknown = containerRef.current;

        if(!isMeasurableNode(node)) {
            return;
        }

        const rect = node.getBoundingClientRect();

        handleSize({
            height: rect.height,
            width: rect.width
        });

        const Observer = getResizeObserver();

        if(!Observer) {
            return;
        }

        const observer = new Observer((entries) => {
            const entry = entries[entries.length - 1];

            if(entry) {
                handleSize({
                    height: entry.contentRect.height,
                    width: entry.contentRect.width
                });
            }
        });

        observer.observe(node);

        return () => {
            observer.disconnect();
        };
    }, [
        handleSize,
        isWeb
    ]);

    useEffect(() => {
        setPlotHeight?.(plotHeight);

        return () => {
            setPlotHeight?.(undefined);
        };
    }, [
        setPlotHeight,
        plotHeight
    ]);

    const handleActiveChange = ({
        source,
        index
    }: ChartPlotActiveChangeConstantType) => {
        setInputSource(source);

        if(!isControlled) {
            setActiveIndexState(index);
        }

        if(index !== activeIndexRef.current) {
            activeIndexRef.current = index;

            onActiveChange?.(index);
        }
    };

    const handleClear = () => {
        handleActiveChange({
            source: inputSource ?? "pointer",
            index: undefined
        });

        setActiveSeriesIndex(0);

        setPointerY(undefined);
    };

    const handleMeasureOrigin = useCallback((onMeasured?: () => void) => {
        containerRef.current?.measureInWindow((x, y) => {
            originRef.current = {
                x,
                y
            };

            onMeasured?.();
        });
    }, []);

    const handlePoint = (point: ChartPlotPoint, source: ChartInputSource) => {
        if(!isInteractive) {
            return;
        }

        handleActiveChange({
            index: getIndex(point, layout),
            source
        });

        setPointerY((current) => current !== undefined && Math.abs(current - point.y) < shapeConfigs.tapSlop ? current : point.y);
    };

    const handlePointRef = useRef(handlePoint);

    handlePointRef.current = handlePoint;

    const getPointFromEvent = (event: ChartPlotPointerEventType): ChartPlotPoint => {
        const rect = event.currentTarget.getBoundingClientRect();

        return {
            x: event.nativeEvent.clientX - rect.left,
            y: event.nativeEvent.clientY - rect.top
        };
    };

    const getPointerSource = (event: ChartPlotPointerEventType): ChartInputSource => {
        return event.nativeEvent.pointerType === CHART_PLOT_TOUCH_POINTER ? "touch" : "pointer";
    };

    const onLayout = (event: LayoutChangeEvent) => {
        handleSize({
            height: event.nativeEvent.layout.height,
            width: event.nativeEvent.layout.width
        });

        if(!isWeb) {
            handleMeasureOrigin();
        }
    };

    const onPointerMove = (event: ChartPlotPointerEventType) => {
        handlePoint(getPointFromEvent(event), getPointerSource(event));
    };

    const onPointerDown = (event: ChartPlotPointerEventType) => {
        onPointerMove(event);
    };

    const onPointerLeave = (event: ChartPlotPointerEventType) => {
        if(getPointerSource(event) === "touch") {
            return;
        }

        handleClear();
    };

    const handleStep = (step: number, source: ChartInputSource) => {
        if(!isInteractive) {
            return;
        }

        const current = activeIndexRef.current;

        const nextIndex = current === undefined ? step > 0 ? 0 : pointCount - 1 : Math.min(pointCount - 1, Math.max(0, current + step));

        handleActiveChange({
            index: nextIndex,
            source
        });
    };

    const onKeyDown = (event: ChartPlotKeyboardEventType) => {
        if(!isInteractive) {
            return;
        }

        if(event.key === "Escape") {
            if(activeIndexRef.current === undefined) {
                return;
            }

            handleActiveChange({
                source: "keyboard",
                index: undefined
            });

            event.preventDefault();

            return;
        }

        if(event.key === "ArrowUp" || event.key === "ArrowDown") {
            if(seriesCount < 2) {
                return;
            }

            const step = event.key === "ArrowDown" ? 1 : -1;

            setInputSource("keyboard");

            setActiveSeriesIndex((current) => (current + step + seriesCount) % seriesCount);

            event.preventDefault();

            return;
        }

        const nextIndex = getChartKeyboardIndex({
            index: activeIndexRef.current,
            count: pointCount,
            key: event.key,
            direction
        });

        if(nextIndex === undefined) {
            return;
        }

        handleActiveChange({
            source: "keyboard",
            index: nextIndex
        });

        event.preventDefault();
    };

    const onAccessibilityAction: ChartPlotNativePropsType["onAccessibilityAction"] = ({
        nativeEvent
    }) => {
        if(nativeEvent.actionName === "increment") {
            handleStep(1, "keyboard");

            return;
        }

        if(nativeEvent.actionName === "decrement") {
            handleStep(-1, "keyboard");
        }
    };

    const onBlur: typeof interactionHandlers.onBlur = (event) => {
        interactionHandlers.onBlur(event);

        handleClear();
    };

    const handleTouch = useCallback((event: GestureResponderEvent, gestureState?: PanResponderGestureState) => {
        const pageX = gestureState?.moveX || event.nativeEvent.pageX;

        const pageY = gestureState?.moveY || event.nativeEvent.pageY;

        handlePointRef.current({
            x: pageX - originRef.current.x,
            y: pageY - originRef.current.y
        }, "touch");
    }, []);

    const panResponder = useMemo(() => PanResponder.create({
        onMoveShouldSetPanResponder: (_event, gestureState) => Math.abs(gestureState.dx) > Math.abs(gestureState.dy),
        onPanResponderGrant: (event) => handleMeasureOrigin(() => handleTouch(event)),
        onPanResponderMove: (event, gestureState) => handleTouch(event, gestureState),
        onPanResponderTerminationRequest: () => true,
        onStartShouldSetPanResponder: () => !isWeb
    }), [
        handleMeasureOrigin,
        handleTouch,
        isWeb
    ]);

    useEffect(() => {
        if(activeIndex === undefined || activeIndex < pointCount) {
            return;
        }

        handleActiveChange({
            source: inputSource ?? "pointer",
            index: undefined
        });
    }, [
        pointCount,
        activeIndex
    ]);

    const tooltipContent = activeIndex === undefined || !isInteractive ? undefined : getTooltip?.(activeIndex, activeSeriesIndex);

    const rawAnchor = activeIndex === undefined || !isInteractive ? undefined : getAnchor(activeIndex, activeSeriesIndex, layout);

    const anchor = rawAnchor && inputSource === "pointer" && pointerY !== undefined ? {
        ...rawAnchor,
        y: pointerY
    } : rawAnchor;

    const isAnnounced = inputSource === "keyboard" || inputSource === "touch";

    const reading = activeIndex === undefined || !isAnnounced ? "" : getReading?.(activeIndex, activeSeriesIndex) ?? "";

    const tooltipPosition = anchor && tooltipSize ? getChartTooltipPosition({
        margin: spaces.spacingSm,
        behaviour: tooltipMode,
        gap: spaces.spacingMd,
        tooltip: tooltipSize,
        container: size,
        anchor,
        isRTL
    }) : undefined;

    const {
        isRendered: isTooltipRendered,
        progress: tooltipProgress,
        isReduceMotionEnabled
    } = usePresence({
        isVisible: !!tooltipContent && !!anchor,
        enterIntent: "surfaceEnter",
        exitIntent: "surfaceExit",
        customTheme
    });

    if(tooltipContent && tooltipPosition && tooltipSize && anchor) {
        tooltipSnapshotRef.current = {
            isOnEnd: tooltipPosition.left + tooltipSize.width / 2 >= anchor.x,
            position: {
                x: tooltipPosition.left,
                y: tooltipPosition.top
            },
            content: tooltipContent
        };
    }

    if(!tooltipContent && !isTooltipRendered) {
        tooltipSnapshotRef.current = undefined;
    }

    const title = context?.title ?? "";

    const accessibleName = title && !summary.startsWith(title) ? [
        title,
        summary
    ].filter(Boolean).join(". ") : summary || title;

    const webProps: ChartPlotWebPropsType = isWeb ? {
        dataSet: interactionHandlers.dataSet,
        tabIndex: isInteractive ? 0 : -1,
        "aria-label": accessibleName,
        "aria-describedby": hintID,
        role: "application",
        onPointerLeave,
        onPointerDown,
        onPointerMove,
        onKeyDown
    } : {};

    const nativeProps: ChartPlotNativePropsType = isWeb ? {} : {
        accessibilityActions: isInteractive ? CHART_PLOT_ACCESSIBILITY_ACTIONS : undefined,
        accessibilityRole: isInteractive ? "adjustable" : undefined,
        accessibilityLabel: accessibleName,
        accessibilityValue: reading ? {
            text: reading
        } : undefined,
        onAccessibilityAction,
        accessible: true
    };

    const svgContent = useMemo(() => {
        if(size.width <= 0 || size.height <= 0) {
            return null;
        }

        return children(layout);
    }, [
        layout.surfaceColor,
        activeSeriesIndex,
        entrance.progress,
        entrance.opacity,
        size.height,
        size.width,
        activeIndex,
        inputSource,
        children,
        isRTL
    ]);

    const renderTooltip = () => {
        const snapshot = tooltipSnapshotRef.current;

        const content = tooltipContent ?? snapshot?.content;

        if(!content || !isTooltipRendered) {
            return null;
        }

        return <Animated.View
            style={[
                stylesheet.tooltip,
                getChartPlotTooltipMotionStyle({
                    left: tooltipPosition?.left ?? snapshot?.position.x ?? 0,
                    top: tooltipPosition?.top ?? snapshot?.position.y ?? 0,
                    isOnEnd: snapshot?.isOnEnd ?? true,
                    progress: tooltipProgress,
                    offset: spaces.spacingXs,
                    isReduceMotionEnabled
                }),
                tooltipPosition || snapshot ? undefined : stylesheet.unmeasured
            ]}
            onLayout={(event) => setTooltipSize({
                height: event.nativeEvent.layout.height,
                width: event.nativeEvent.layout.width
            })}
        >
            <ChartTooltip
                customTheme={customTheme}
                title={content.title}
                rows={content.rows}
                sum={content.sum}
            >
                {renderTooltipProp?.(content)}
            </ChartTooltip>
        </Animated.View>;
    };

    const renderBody = () => {
        if(isFirstLoad) {
            const modelKind = context?.modelRef.current?.kind;

            return <ChartSkeleton
                kind={skeletonKind ?? (modelKind ? CHART_SKELETON_KINDS[modelKind] : "bars")}
                customLocalize={customLocalizeProp}
                customTheme={customTheme}
                height={plotHeight}
            />;
        }

        return <>
            <View
                importantForAccessibility="no-hide-descendants"
                accessibilityElementsHidden={true}
                style={[
                    stylesheet.svg,
                    svgDynamicStyle
                ]}
                aria-hidden={true}
            >
                {
                    svgContent ?
                        <Svg
                            {...CHART_PLOT_SVG_PROPS}
                            height={size.height}
                            width={size.width}
                        >
                            {svgContent}
                        </Svg>
                    :
                        null
                }
            </View>
            {renderTooltip()}
        </>;
    };

    return <View
        {...props}
        {...webProps}
        {...nativeProps}
        {...(isWeb ? {} : panResponder.panHandlers)}
        onFocus={interactionHandlers.onFocus}
        style={[
            style,
            stylesheet.container,
            containerDynamicStyle
        ]}
        focusable={isInteractive}
        onLayout={onLayout}
        ref={containerRef}
        onBlur={onBlur}
    >
        {renderBody()}
        {
            isWeb ?
                <>
                    <Text
                        style={[
                            stylesheet.live,
                            liveDynamicStyle
                        ]}
                        aria-hidden={true}
                        id={hintID}
                    >
                        {localize("chart-keyboard-hint")}
                    </Text>
                    <Text
                        style={[
                            stylesheet.live,
                            liveDynamicStyle
                        ]}
                        aria-live="polite"
                        id={liveID}
                    >
                        {reading}
                    </Text>
                </>
            :
                null
        }
    </View>;
};
export default ChartPlot;
